import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { WeeklyOperatingHours, DayOperatingHours, createDefaultOperatingHours } from '@/components/common/WeeklyOperatingHours';
import { sellerService } from '@/services/seller';
import { getRestaurantStatus, toggleRestaurantStatus } from '@/services/seller/seller-store';
import { useToast } from '@/components/common/Toast';
import { useSellerStore } from '@/store/seller';
import { BusinessStatusModal } from '@/components/seller/BusinessStatusModal';

export function OperatingHoursPage() {
  const navigate = useNavigate();
  const { showToast, ToastContainer } = useToast();
  const { setLoading, setError } = useSellerStore();

  const [restaurantId, setRestaurantId] = useState<string>('');
  const [operatingHours, setOperatingHours] = useState<DayOperatingHours[]>(createDefaultOperatingHours());
  const [isSaving, setIsSaving] = useState(false);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [isTogglingStatus, setIsTogglingStatus] = useState(false);

  useEffect(() => {
    loadOperatingHours();
  }, []);

  const loadOperatingHours = async () => {
    try {
      setLoading(true);
      
      // 1. 레스토랑 ID 가져오기
      const restaurantResult = await sellerService.getSellerRestaurantId();
      if (!restaurantResult.success || !restaurantResult.restaurantId) {
        setError(restaurantResult.error || '레스토랑 정보를 찾을 수 없습니다');
        return;
      }

      setRestaurantId(restaurantResult.restaurantId);

      // 2. 영업 상태 가져오기
      const statusResult = await getRestaurantStatus(restaurantResult.restaurantId);
      if (statusResult.success && statusResult.data) {
        setIsOpen(statusResult.data.is_open);
      }

      // 3. 영업시간 정보 가져오기
      const hoursResult = await sellerService.getOperatingHours(restaurantResult.restaurantId);
      if (hoursResult.success && hoursResult.data && Array.isArray(hoursResult.data)) {
        setOperatingHours(hoursResult.data);
      } else {
        // 데이터가 없거나 올바르지 않은 경우 기본값 유지
        console.log('No operating hours data found, using default values');
        setOperatingHours(createDefaultOperatingHours());
      }
      
    } catch (error) {
      console.error('Operating hours load error:', error);
      setError('영업시간 정보를 불러오는 중 오류가 발생했습니다');
      // 에러 발생 시에도 기본값 사용
      setOperatingHours(createDefaultOperatingHours());
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!restaurantId) {
      showToast('레스토랑 정보를 찾을 수 없습니다');
      return;
    }

    try {
      setIsSaving(true);

      const result = await sellerService.updateOperatingHours(restaurantId, operatingHours);
      
      if (result.success) {
        showToast('영업시간이 성공적으로 저장되었습니다');
      } else {
        showToast(result.error || '영업시간 저장에 실패했습니다');
      }
    } catch (error) {
      console.error('Operating hours save error:', error);
      showToast('영업시간 저장 중 오류가 발생했습니다');
    } finally {
      setIsSaving(false);
    }
  };

  const handleStatusToggle = () => {
    setShowStatusModal(true);
  };

  const handleConfirmStatusToggle = async () => {
    if (!restaurantId) return;

    try {
      setIsTogglingStatus(true);
      const newStatus = !isOpen;
      
      const result = await toggleRestaurantStatus(restaurantId, newStatus);
      
      if (result.success) {
        setIsOpen(newStatus);
        setShowStatusModal(false);
        showToast(newStatus ? '영업을 시작했습니다' : '영업을 중단했습니다');
      } else {
        showToast(result.error || '영업 상태 변경에 실패했습니다');
      }
    } catch (error) {
      console.error('Status toggle error:', error);
      showToast('영업 상태 변경 중 오류가 발생했습니다');
    } finally {
      setIsTogglingStatus(false);
    }
  };

  return (
    <div className="flex flex-col h-screen bg-[#F5F5F5]">
      {/* 스크롤 가능한 메인 콘텐츠 영역 */}
      <main className="flex-grow overflow-y-auto pb-24">
        <div 
          className="min-h-screen bg-white"
          style={{ 
            paddingTop: '16px',
            paddingLeft: '16px',
            paddingRight: '16px'
          }}
        >
          {/* 헤더 */}
          <div className="mb-6">
            <div className="flex items-center mb-4">
              <button
                onClick={() => navigate(-1)}
                className="p-2 text-gray-600 hover:text-gray-800 mr-4"
              >
                ←
              </button>
              <h1 className="text-[24px] font-bold text-gray-800 flex-1 text-center mr-12">영업시간</h1>
            </div>
            
            {/* 영업 상태 버튼 */}
            <div className="flex justify-center">
              <button
                onClick={handleStatusToggle}
                disabled={!restaurantId}
                className={`px-6 py-3 rounded-lg font-medium text-white transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed ${
                  isOpen 
                    ? 'bg-red-500 hover:bg-red-600' 
                    : 'bg-green-500 hover:bg-green-600'
                }`}
              >
                {isOpen ? '영업 중단하기' : '영업 시작하기'}
              </button>
            </div>
          </div>

          {/* 영업시간 설정 컴포넌트 */}
          <div className="bg-white rounded-lg shadow-sm">
            <WeeklyOperatingHours
              operatingHours={operatingHours}
              onChange={setOperatingHours}
            />
          </div>
        </div>
      </main>

      {/* 하단 고정 저장 버튼 */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4 z-50">
        <div className="max-w-md mx-auto">
          <button
            onClick={handleSave}
            disabled={isSaving || !restaurantId}
            className="w-full py-3 bg-green-500 text-white rounded-lg font-medium hover:bg-green-600 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
          >
            {isSaving ? '저장 중...' : '저장하기'}
          </button>
        </div>
      </div>

      <BusinessStatusModal
        isOpen={showStatusModal}
        onClose={() => setShowStatusModal(false)}
        onConfirm={handleConfirmStatusToggle}
        isClosing={isOpen}
        isLoading={isTogglingStatus}
      />

      <ToastContainer />
    </div>
  );
}